import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { toast } from "react-toastify";

import AppLayout from "../../components/layout/AppLayout";
import Breadcrumb from "../../components/layout/Breadcrumb";
import VmcOrderForm from "../../components/vmcOrders/VmcOrderForm";
import { pageTitleClass } from "../../components/ui/styles";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { getLoggingUser } from "../../features/loggingUser/loggingUserSlice";
import {
  clearVmcOrderPartyInfo,
  fetchVmcOrderParties,
  fetchVmcOrderPartyInfo,
  getVmcOrderPartyInfo,
  getVmcOrderPartyInfoLoading,
  getVmcOrderPartyOptions,
} from "../../features/vmcOrders/vmcOrdersSlice";
import { POST } from "../../services/axios.service";
import { hasPermission, permission } from "../../static/permission";
import { route } from "../../static/route";
import { toastStyles } from "../../static/styles";
import { vmcOrderLinePayload } from "../../static/vmcLine";
import { BreadcrumbType } from "../../types/common";
import { IVmcOrderFormInput } from "../../types/vmcOrder";

const breadcrumbItems: BreadcrumbType[] = [
  { label: "હોમ", href: route.dashboard },
  { label: "VMC ઓર્ડર", href: route.vmcOrders },
  { label: "નવો ઓર્ડર" },
];

const VmcOrderCreatePage = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const partyOptions = useAppSelector(getVmcOrderPartyOptions);
  const partyInfo = useAppSelector(getVmcOrderPartyInfo);
  const partyInfoLoading = useAppSelector(getVmcOrderPartyInfoLoading);
  const loggingUser = useAppSelector(getLoggingUser);

  const withRates = hasPermission(
    loggingUser?.permissions,
    permission.vmcRate
  );

  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    dispatch(fetchVmcOrderParties());
    dispatch(clearVmcOrderPartyInfo());
  }, [dispatch]);

  const handlePartyChange = useCallback(
    (partyId: number) => {
      if (partyId) {
        dispatch(fetchVmcOrderPartyInfo({ party_id: partyId }));
      } else {
        dispatch(clearVmcOrderPartyInfo());
      }
    },
    [dispatch]
  );

  const handleSubmit = async (data: IVmcOrderFormInput) => {
    setIsSubmitting(true);
    try {
      await POST("/v1/admin/vmc-orders", {
        party_id: data.party_id,
        date: moment(data.date).format("YYYY-MM-DD HH:mm:ss"),
        items: vmcOrderLinePayload(data.items, withRates),
      })
        .then((response: any) => {
          if (response.data.status === "success") {
            toast.success(response.data.message, {
              ...(toastStyles as any),
            });
            navigate(route.vmcOrders);
          } else {
            toast.error(response.data.message, {
              ...(toastStyles as any),
            });
          }
        })
        .catch((error: any) => {
          toast.error(error.data.message, {
            ...(toastStyles as any),
          });
        });
    } catch (error) {
      toast.error("કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.", {
        ...(toastStyles as any),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AppLayout>
      <div className="mb-2 md:mb-0">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      <div className="flex items-center mb-4 md:mb-6 mt-2">
        <h1 className={pageTitleClass}>નવો VMC ઓર્ડર ઉમેરો</h1>
      </div>

      <div className="flex flex-col gap-6">
        <VmcOrderForm
          partyOptions={partyOptions}
          partyInfo={partyInfo}
          partyInfoLoading={partyInfoLoading}
          onPartyChange={handlePartyChange}
          onSubmit={handleSubmit}
          isSubmitting={isSubmitting}
          onCancel={() => navigate(route.vmcOrders)}
          submitLabel="સાચવો"
          withRates={withRates}
        />
      </div>
    </AppLayout>
  );
};
export default VmcOrderCreatePage;
